import React, { useRef, useEffect, useState } from "react";
import { useSpring, animated } from "@react-spring/web";
import _ from "lodash";
import TestimonialCard, { TestimonialData } from "./TestimonialCard";
import styles from "./TestimonialsDesktop.module.scss";
import ShowMoreCard, { ShowMoreCardProps } from "./ShowMoreCard";


interface TestimonialsDesktopProps {
  testimonials: TestimonialData[];
  showSeeMorCard?: boolean;
}

const TestimonialsDesktop: React.FC<TestimonialsDesktopProps & ShowMoreCardProps> = ({
  testimonials,
  showSeeMorCard = false,
  reactGA
}) => {
  const isEven = (testimonials.length + (showSeeMorCard ? 1 : 0)) % 2 === 0;
  const factor = 1;
  const sectionRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const extraContentRef = useRef<HTMLDivElement>(null);
  const diff = useRef<number>(0);

  const [height, setHeight] = useState<number>(0);
  const [translate, setTranslate] = useState<number>(0);
  const [progress, setProgress] = useState<number>(0);

  const handleScroll = () => {
    const content = contentRef.current;
    const container = containerRef.current;
    const section = sectionRef.current;

    if (!content || !container || !section) return;

    diff.current = (Math.max(content.offsetWidth - window.innerWidth, 0)) * factor;

    const scrollBottom = window.scrollY + window.innerHeight;
    const containerHeight = container.offsetHeight;
    const containerBottom = section.offsetTop + containerHeight;

    const rawProgress = (scrollBottom - containerBottom) / diff.current;
    const newProgress = Math.min(1, Math.max(0, rawProgress));
    const newHeight = diff.current * newProgress;
    const newTranslate = (diff.current / factor) * newProgress * (isEven ? 2 : 1);

    setProgress(rawProgress);
    setHeight(newHeight);
    setTranslate(newTranslate);
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    handleScroll();
  }, []);

  const gridSpring = useSpring({
    transform: `translateX(-${translate}px)`,
    config: { tension: 200, friction: 30 }
  });

  return (
    <div
      ref={sectionRef}
      className={styles.testimonialsSection}
    >

      <div style={{ height: `${containerRef.current?.offsetHeight}px` }} >
        <div
          ref={containerRef}
          className={styles.testimonialsContainer}
          style={{
            // eslint-disable-next-line no-nested-ternary
            position: progress > 0 && progress < 1 ? "fixed" : progress >= 1 ? "absolute" : "unset",
            bottom: progress > 0 ? "0" : "unset"
          }}
        >
          <animated.div
            ref={contentRef}
            className={styles.testimonials}
            style={{
              ...gridSpring,
              gridTemplateColumns: _.range(1, Math.ceil((testimonials.length + (showSeeMorCard ? 1 : 0)) / 2) + 1).map(() => "1fr").join(" ")
            }}
          >
            {!showSeeMorCard && <>
              {testimonials.map((item, index) => (
                <div
                  key={index}
                  className={`${styles.testimonialItem}${(item.alignEnd ?? false) ? ` ${styles["testimonialItem--end"]}` : ""}`}>
                  <TestimonialCard data={item} small={item.small ?? false} margin={item.margin ?? false} />
                </div>
              ))}
            </>}

            {showSeeMorCard && <>
              {testimonials.slice(0, testimonials.length / 2).map((item, index) => (
                <div
                  key={index + 1}
                  className={`${styles.testimonialItem}${(item.alignEnd ?? false) ? ` ${styles["testimonialItem--end"]}` : ""}`}>
                  <TestimonialCard data={item} small={item.small ?? false} margin={item.margin ?? false} />
                </div>
              ))}

              <div
                className={`${styles.testimonialItem}`}
                style={{
                  width: window.innerWidth
                }}
              >
                <ShowMoreCard reactGA={reactGA} progress={progress} />
              </div>

              {testimonials.slice(testimonials.length / 2).map((item, index) => (
                <div
                  key={(index + 1) * 2}
                  className={`${styles.testimonialItem}${(item.alignEnd ?? false) ? ` ${styles["testimonialItem--end"]}` : ""}`}>
                  <TestimonialCard data={item} small={item.small ?? false} margin={item.margin ?? false} />
                </div>
              ))}
            </>}
          </animated.div>
        </div>
      </div>

      <div ref={extraContentRef} style={{ height: `${height}px` }}/>

    </div>
  );
};

export default TestimonialsDesktop;
